import { AxCustomer, IAxCustomer } from '@/models/axdata/AxCustomer';
import { AxMachineModel } from '@/models/axdata/AxMachineModel';
import { IDexieAxCostumer } from '@/models/dexie/DexieAxCostumer';
import { AxiosService, OnlineService } from './index';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { AuthStore } from '@/store/store.auth';
import { ApiEndpoints } from '@/config/index';
import { LoggerService } from '@/utils';
import { IDexieConfigurationUpdates, DexieConfigurationUpdates } from '@/models/dexie/DexieConfigurationUpdates';
import { Helpers } from '@/helpers/Helpers';
import { AxCostumers } from '@/models/axdata/AxCustomers';
import { DexieAxCostumer } from '@/models/dexie/DexieAxCostumer';
import { AxMachineModels } from '@/models/axdata/AxMachineModels';
import { DexieAxMachineModel } from '@/models/dexie/DexieAxMachineModel';

import DexieDB from './DexieDB';
import DexieDBProvider from '@/services/DexieDBProvider';
import { ConfigurationValueSelect } from '@/models/configuration/ValuesSelect';
import { ILiteEvent, LiteEvent } from '@/models/event/liteEvent';
import { AxMachine } from '@/models/axdata/AxMachine';

class AxDataProvider {
    public axCustomers: AxCustomer[] = [];
    public axMachineModels: AxMachineModel[] = [];

    public axAllCountries: ConfigurationValueSelect[] = [];

    public axFilteredCustomers: ConfigurationValueSelect[] = [];

    public selectedCustomer: string | undefined;
    /**
     * For filters we need a list of costumers
     */
    public selectedCustomers: string[] | undefined;
    public selectedCustomerData: AxCustomer[] = [];
    public selectedCustomerNumbers: ConfigurationValueSelect[] = [];
    public selectedCustomerProjectNumbers: ConfigurationValueSelect[] = [];
    public selectedCustomerCountries: ConfigurationValueSelect[] = [];
    public selectedCustomerAddresses: ConfigurationValueSelect[] = [];

    private axMachineModelsForFilters: AxMachineModel[] = [];

    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;
    private readonly onSelectedCustomerChanged = new LiteEvent<string | undefined>();
    private readonly onSelectedCustomersChanged = new LiteEvent<string[] | undefined>();
    private readonly onAxFilteredCustomersChanged = new LiteEvent<ConfigurationValueSelect[]>();
    private readonly onAxFilterCustomersStarted = new LiteEvent<void>();

    // tslint:disable-next-line: no-empty
    constructor() {}

    /**
     * Delete all customers from dexie db
     */
    public async GetAllCustomers(): Promise<DexieAxCostumer[]> {
        return this.dexieDB.getTable<DexieAxCostumer>(this.dexieDB!.getTables().axCustomers).toArray();
    }

    /**
     * Delete all customers from dexie db
     */
    public async DeleteAllCustomers(): Promise<void> {
        return this.dexieDB.clear<IDexieAxCostumer>(this.dexieDB!.getTables().axCustomers);
    }
    /**
     * Set new costumers
     * @param inDexieAxCostumers
     */
    public async SetNewCustomers(inDexieAxCostumers: DexieAxCostumer[]): Promise<number> {
        return this.dexieDB.bulkPut<DexieAxCostumer>(this.dexieDB!.getTables().axCustomers, inDexieAxCostumers);
    }

    /**
     * Delete all machine models from dexie db
     */
    public async DeleteAllMachineModels(): Promise<void> {
        return this.dexieDB.clear<IDexieAxCostumer>(this.dexieDB!.getTables().axMachineModels);
    }
    /**
     * Set new costumers
     * @param inDexieAxMachineModels
     */
    public async SetNewMachineModels(inDexieAxMachineModels: DexieAxMachineModel[]): Promise<number> {
        return this.dexieDB.bulkPut<DexieAxMachineModel>(this.dexieDB!.getTables().axMachineModels, inDexieAxMachineModels);
    }

    /**
     * Set/update the last update date for the cutomer data
     * @param inUpdate
     */
    public async SetLastUpdateDate(inUpdate: DexieConfigurationUpdates): Promise<number> {
        return this.dexieDB.put<DexieConfigurationUpdates>(this.dexieDB!.getTables().configurationUpdates, inUpdate);
    }

    /**
     * Event getter for unit updates
     */
    public get OnSelectedCustomerChanged() {
        return this.onSelectedCustomerChanged.expose();
    }

    public get OnSelectedCustomersChanged() {
        return this.onSelectedCustomersChanged.expose();
    }

    public get OnAxFilteredCustomersChanged(): ILiteEvent<ConfigurationValueSelect[]> {
        return this.onAxFilteredCustomersChanged.expose();
    }

    public get OnAxFilterCustomersStarted(): ILiteEvent<void> {
        return this.onAxFilterCustomersStarted.expose();
    }

    public async GetAllCustomerNames(): Promise<ConfigurationValueSelect[]> {
        this.onAxFilterCustomersStarted.trigger();
        const allCustomners = await this.dexieDB.getTable<DexieAxCostumer>(this.dexieDB!.getTables().axCustomers).toArray();

        return new Promise<ConfigurationValueSelect[]>((resolve, reject) => {
            const data: ConfigurationValueSelect[] = [];

            allCustomners
                .filter((customer, index, self) => index === self.findIndex((t) => t.customerName === customer.customerName))
                .forEach((customer) => {
                    data.push(
                        new ConfigurationValueSelect({
                            text: customer.customerName,
                            value: customer.customerName,
                        }),
                    );
                    customer.projects.forEach((project) => {
                        if (this.axAllCountries.some((c) => c.value === project.countryCode) === false) {
                            this.axAllCountries.push(
                                new ConfigurationValueSelect({
                                    text: project.countryName,
                                    value: project.countryCode,
                                }),
                            );
                        }
                    });
                });
            this.axFilteredCustomers = data;
            this.onAxFilteredCustomersChanged.trigger(data);
            resolve(data);
        });
    }

    public async FilterCustomersBySerialnumber(inSerialnumber: string, triggerEvents: boolean = true): Promise<ConfigurationValueSelect[]> {
        this.onAxFilterCustomersStarted.trigger();
        const allCustomners = await this.dexieDB.getTable<DexieAxCostumer>(this.dexieDB!.getTables().axCustomers).toArray();

        return new Promise<ConfigurationValueSelect[]>((resolve, reject) => {
            const data: ConfigurationValueSelect[] = [];

            allCustomners.forEach((customer) => {
                customer.projects.forEach((project) => {
                    const machines = project.machines.filter((machine) => machine.serialNumber && machine.serialNumber.includes(inSerialnumber));
                    if (machines.length > 0) {
                        data.push(
                            new ConfigurationValueSelect({
                                text: customer.customerName,
                                value: customer.customerName,
                            }),
                        );
                    }
                });
            });

            if (data.length > 0) {
                if (triggerEvents) {
                    this.axFilteredCustomers = data;
                    this.onAxFilteredCustomersChanged.trigger(data);
                }
                resolve(data);
            } else {
                reject('no customer found');
                if (triggerEvents) {
                    this.onAxFilteredCustomersChanged.trigger(data);
                }
            }
        });
    }

    public async GetMachineBySerialnumber(inSerialnumber: string): Promise<AxMachineModel> {
        this.onAxFilterCustomersStarted.trigger();
        const allCustomners = await this.dexieDB.getTable<DexieAxCostumer>(this.dexieDB!.getTables().axCustomers).toArray();

        return new Promise<AxMachineModel>(async (resolve, reject) => {
            let machineModel = '';
            allCustomners.forEach((customer) => {
                customer.projects.forEach((project) => {
                    const machines = project.machines.filter((machine) => machine.serialNumber && machine.serialNumber.includes(inSerialnumber));
                    if (machines.length === 1) {
                        machineModel = machines[0].machineModel;
                    }
                });
            });
            if (machineModel) {
                const machines = await this.dexieDB
                    .getTable<DexieAxMachineModel>(this.dexieDB!.getTables().axMachineModels)
                    .where('model')
                    .equalsIgnoreCase(machineModel)
                    .toArray();
                if (machines && machines.length > 0) {
                    LoggerService.debug(`machine(s) found: `, machines);
                    /**
                     * Mostly the found machines should all be the same
                     * just with different `typeId`.
                     * So we can use index 0
                     */
                    resolve(new AxMachineModel(machines[0]));
                } else {
                    reject(`no machine found for machine model: ${machineModel}`);
                }
            } else {
                reject(`no machine found for serial number. ${inSerialnumber}`);
            }
        });
    }

    public async SetSelectedCustomer(inCustomerName?: string) {
        this.selectedCustomer = inCustomerName != null ? inCustomerName : undefined;
        this.clearSelectedCustomerData();
        if (inCustomerName != null) {
            await this.dexieDB
                .getTable<DexieAxCostumer>(this.dexieDB!.getTables().axCustomers)
                .where('customerName')
                .equals(inCustomerName)
                .each((customer) => {
                    this.selectedCustomerData.push(customer);
                });

            this.selectedCustomerData.forEach((customer) => {
                this.selectedCustomerNumbers.push(
                    new ConfigurationValueSelect({
                        text: customer.customerNumber,
                        value: customer.customerNumber,
                    }),
                );
                customer.projects.forEach((project) => {
                    this.selectedCustomerProjectNumbers.push(
                        new ConfigurationValueSelect({
                            text: project.projectNumber,
                            value: project.projectNumber,
                        }),
                    );
                    if (this.selectedCustomerCountries.some((c) => c.value === project.countryCode) === false) {
                        this.selectedCustomerCountries.push(
                            new ConfigurationValueSelect({
                                text: project.countryName,
                                value: project.countryCode,
                            }),
                        );
                    }
                    if (this.selectedCustomerAddresses.some((c) => c.value === project.address) === false) {
                        this.selectedCustomerAddresses.push(
                            new ConfigurationValueSelect({
                                text: project.address,
                                value: project.address,
                            }),
                        );
                    }
                });
            });
        }
        this.onSelectedCustomerChanged.trigger(this.selectedCustomer);
    }

    /**
     * Method to get data for a list of costumers, especially for filters
     * @param inCustomerNames
     */
    public async SetSelectedCustomers(inCustomerNames?: string[]) {
        this.selectedCustomers = inCustomerNames != null ? inCustomerNames : undefined;
        this.clearSelectedCustomerData();
        if (inCustomerNames != null) {
            await this.dexieDB
                .getTable<DexieAxCostumer>(this.dexieDB!.getTables().axCustomers)
                .where('customerName')
                .startsWithAnyOfIgnoreCase(inCustomerNames)
                .each((customer) => {
                    this.selectedCustomerData.push(customer);
                });

            this.selectedCustomerData.forEach((customer) => {
                this.selectedCustomerNumbers.push(
                    new ConfigurationValueSelect({
                        text: customer.customerNumber,
                        value: customer.customerNumber,
                    }),
                );
                customer.projects.forEach((project) => {
                    this.selectedCustomerProjectNumbers.push(
                        new ConfigurationValueSelect({
                            text: project.projectNumber,
                            value: project.projectNumber,
                        }),
                    );
                    if (this.selectedCustomerCountries.some((c) => c.value === project.countryCode) === false) {
                        this.selectedCustomerCountries.push(
                            new ConfigurationValueSelect({
                                text: project.countryName,
                                value: project.countryCode,
                            }),
                        );
                    }
                    if (this.selectedCustomerAddresses.some((c) => c.value === project.address) === false) {
                        this.selectedCustomerAddresses.push(
                            new ConfigurationValueSelect({
                                text: project.address,
                                value: project.address,
                            }),
                        );
                    }
                });
            });
        }
        this.onSelectedCustomersChanged.trigger(this.selectedCustomers);
    }

    public async getMachineModelsBySection(inSectionIdentifier: string): Promise<AxMachineModel[]> {
        return new Promise<AxMachineModel[]>(async (resolve, reject) => {
            if (this.selectedCustomer !== undefined) {
                const machineModels = await this.dexieDB
                    .getTable<AxMachineModel>(this.dexieDB!.getTables().axMachineModels)
                    .where('machineId')
                    .equals(inSectionIdentifier)
                    .toArray();

                this.selectedCustomerData.forEach((customer) => {
                    customer.projects.forEach((project) => {
                        const machines = project.machines.filter(this.filterMachineModels(machineModels));
                        machines.forEach((machine) => {
                            const machineModel = machineModels.find((m) => m.model === machine.machineModel);
                            if (machineModel !== undefined) {
                                if (machineModel.commissonNumbers === undefined) {
                                    machineModel.commissonNumbers = [];
                                }
                                machineModel.commissonNumbers.push(machine.commissionNumber);
                                if (machine.serialNumber !== undefined) {
                                    if (machineModel.serialNumbers === undefined) {
                                        machineModel.serialNumbers = [];
                                    }
                                    machineModel.serialNumbers.push(machine.serialNumber);
                                }
                            }
                        });
                    });
                });

                resolve(machineModels);
            } else {
                reject('getMachineModelsBySection: No customer selected');
            }
        });
    }

    public async GenerateMachineModelsForFilter(): Promise<AxMachineModel[]> {
        return new Promise<AxMachineModel[]>(async (resolve, reject) => {
            const machineModels = await this.dexieDB.getTable<AxMachineModel>(this.dexieDB!.getTables().axMachineModels).toArray();
            const customers = await this.GetAllCustomers();

            customers.forEach((customer) => {
                customer.projects.forEach((project) => {
                    const machines = project.machines.filter(this.filterMachineModels(machineModels));
                    machines.forEach((machine) => {
                        const machineModel = machineModels.find((m) => m.model === machine.machineModel);
                        if (machineModel !== undefined) {
                            if (machineModel.commissonNumbers === undefined) {
                                machineModel.commissonNumbers = [];
                            }
                            machineModel.commissonNumbers.push(machine.commissionNumber);
                            if (machine.serialNumber !== undefined) {
                                if (machineModel.serialNumbers === undefined) {
                                    machineModel.serialNumbers = [];
                                }
                                machineModel.serialNumbers.push(machine.serialNumber);
                            }
                        }
                    });
                });
            });
            this.axMachineModelsForFilters = machineModels;
            resolve(machineModels);
        });
    }

    public async GetMachineModelsBySectionForFilter(inSectionIdentifier: string): Promise<AxMachineModel[]> {
        return new Promise<AxMachineModel[]>((resolve) => {
            const machineModels = this.axMachineModelsForFilters.filter((machine) => machine.machineId === inSectionIdentifier);
            resolve(machineModels);
        });
    }

    public async UpdateAxData() {
        await this.SyncAxCustomers();
        await this.SyncAxMachineModels();
    }

    private filterMachineModels(inMachineModels: AxMachineModel[]): any {
        return (inMachine: AxMachine) => {
            return inMachineModels.some((imm) => imm.model === inMachine.machineModel);
        };
    }

    private async SyncAxCustomers() {
        const tableStrings = this.dexieDB.getTables();
        let onlineAxCustomers: AxCostumers | undefined;
        let lastUpdate: DexieConfigurationUpdates | undefined;
        let loadData = false;

        await this.dexieDB
            .getTable<IDexieConfigurationUpdates>(tableStrings.configurationUpdates)
            .where('name')
            .equalsIgnoreCase(tableStrings.axCustomers)
            .first((data) => {
                if (data) {
                    LoggerService.LogDebug(`axCustomers updates: `, data);
                    lastUpdate = data;
                }
            })
            .catch((error) => {
                LoggerService.LogDebug(`WARN: `, error);
            });

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
            if (lastUpdate !== undefined) {
                await AxiosService.get(ApiEndpoints.configurationAxCustomers(true), null).then(async (response) => {
                    if (response.data && response.data.date) {
                        if (lastUpdate !== undefined && response.data.date && Helpers.compareDateStrings(response.data.date, lastUpdate.date) > 0) {
                            loadData = true;
                            lastUpdate.date = response.data.date;
                        }
                    }
                });
            } else {
                loadData = true;
            }

            if (loadData) {
                await AxiosService.get(ApiEndpoints.configurationAxCustomers(false), null)
                    .then((innerResponse) => {
                        onlineAxCustomers = new AxCostumers(innerResponse.data);
                    })
                    .catch((error) => {
                        LoggerService.LogError(`error on fetching ax customers: `, error);
                    });
            }
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`, OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        if (loadData === true && onlineAxCustomers !== undefined && onlineAxCustomers.customers !== undefined && onlineAxCustomers.customers.length > 0) {
            /** first delete all saved customer data */
            await this.DeleteAllCustomers().catch((error) => {
                LoggerService.LogError(`Error on deleting old customers: `, error);
            });
            await this.SetNewCustomers(onlineAxCustomers.customers).catch((error) => {
                LoggerService.LogError(`Error on setting new customers: `, error);
            });

            if (lastUpdate === undefined) {
                lastUpdate = new DexieConfigurationUpdates({
                    name: tableStrings.axCustomers,
                    date: onlineAxCustomers.date,
                });
            }
            await this.SetLastUpdateDate(lastUpdate).catch((error) => {
                LoggerService.LogError(`Error on setting last update for customers: `, error);
            });
        }
    }

    private async SyncAxMachineModels() {
        const tableStrings = this.dexieDB.getTables();
        let onlineAxMachineModels: AxMachineModels | undefined;
        let lastUpdate: DexieConfigurationUpdates | undefined;
        let loadData = false;

        await this.dexieDB
            .getTable<IDexieConfigurationUpdates>(tableStrings.configurationUpdates)
            .where('name')
            .equalsIgnoreCase(tableStrings.axMachineModels)
            .first((data) => {
                if (data) {
                    LoggerService.LogDebug(`axMachineModels updates: `, data);
                    lastUpdate = data;
                }
            })
            .catch((error) => {
                LoggerService.LogDebug(`WARN: `, error);
            });

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
            if (lastUpdate !== undefined) {
                await AxiosService.get(ApiEndpoints.configurationAxMachineModels(true), null).then(async (response) => {
                    if (response.data && response.data.date) {
                        if (lastUpdate !== undefined && response.data.date && Helpers.compareDateStrings(response.data.date, lastUpdate.date) > 0) {
                            loadData = true;
                            lastUpdate.date = response.data.date;
                        }
                    }
                });
            } else {
                loadData = true;
            }

            if (loadData) {
                await AxiosService.get(ApiEndpoints.configurationAxMachineModels(false), null)
                    .then((innerResponse) => {
                        onlineAxMachineModels = new AxMachineModels(innerResponse.data);
                    })
                    .catch((error) => {
                        LoggerService.LogError(`error on fetching ax customers: `, error);
                    });
            }
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`, OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        if (loadData === true && onlineAxMachineModels !== undefined && onlineAxMachineModels.machineModels !== undefined && onlineAxMachineModels.machineModels.length > 0) {
            /** first delete all saved customer data */
            await this.DeleteAllMachineModels().catch((error) => {
                LoggerService.LogError(`Error on deleting old customers: `, error);
            });
            await this.SetNewMachineModels(onlineAxMachineModels.machineModels).catch((error) => {
                LoggerService.LogError(`Error on setting new customers: `, error);
            });

            if (lastUpdate === undefined) {
                lastUpdate = new DexieConfigurationUpdates({
                    name: tableStrings.axMachineModels,
                    date: onlineAxMachineModels.date,
                });
            }
            await this.SetLastUpdateDate(lastUpdate).catch((error) => {
                LoggerService.LogError(`Error on setting last update for customers: `, error);
            });
        }
    }

    private clearSelectedCustomerData() {
        this.selectedCustomerData = [];
        this.selectedCustomerCountries = [];
        this.selectedCustomerNumbers = [];
        this.selectedCustomerProjectNumbers = [];
        this.selectedCustomerAddresses = [];
    }
}

export default AxDataProvider;
